// The building blocks of the handoff guide: one control per kind of thing the
// receiver does in the Play Console — paste a text, pick an answer, upload a
// file — so every step reads the same way and nothing has to be retyped.
import { useState, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Icons (inline, 20px grid, stroke follows currentColor)

const svg = (d: ReactNode, cls = 'h-4 w-4') => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"
    strokeLinejoin="round" className={cls} aria-hidden="true">{d}</svg>
);
export const IconCheck = ({ className }: { className?: string }) => svg(<path d="M20 6 9 17l-5-5" />, className);
export const IconCopy = ({ className }: { className?: string }) =>
  svg(<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>, className);
export const IconDownload = ({ className }: { className?: string }) =>
  svg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /><path d="M12 15V3" /></>, className);
export const IconLeft = ({ className }: { className?: string }) => svg(<path d="m15 18-6-6 6-6" />, className);
export const IconRight = ({ className }: { className?: string }) => svg(<path d="m9 18 6-6-6-6" />, className);
export const IconList = ({ className }: { className?: string }) =>
  svg(<><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>, className);
export const IconX = ({ className }: { className?: string }) => svg(<path d="M18 6 6 18M6 6l12 12" />, className);

// ---------------------------------------------------------------------------
// Clipboard

/**
 * navigator.clipboard is missing on plain http and refused in some in-app
 * browsers (Telegram's among them); the textarea route still works there.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// Layout pieces

/** Where the step happens in the console's left menu. */
export function Where({ path }: { path: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-content-subtle">In Play Console:</span>
      {path.map((p, i) => (
        <span key={p} className="flex items-center gap-1.5">
          {i > 0 && <IconRight className="h-3 w-3 text-content-subtle" />}
          <span className="rounded-md bg-surface-muted px-2 py-0.5 font-medium text-content">{p}</span>
        </span>
      ))}
    </div>
  );
}

/** Numbered actions, in the order the console asks for them. */
export function Actions({ children }: { children: ReactNode }) {
  return <ol className="space-y-4 [counter-reset:act]">{children}</ol>;
}

export function Act({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <li className="relative pl-9 [counter-increment:act] before:absolute before:left-0 before:top-0 before:flex
      before:h-6 before:w-6 before:items-center before:justify-center before:rounded-full before:bg-surface-muted
      before:text-xs before:font-semibold before:text-content-muted before:content-[counter(act)]">
      <p className="text-sm leading-6 text-content">{title}</p>
      {children && <div className="mt-2 space-y-2">{children}</div>}
    </li>
  );
}

export function Callout({ tone = 'info', title, children }: {
  tone?: 'info' | 'warning' | 'success'; title?: string; children: ReactNode;
}) {
  const tones = {
    info: 'border-info/30 bg-info-soft text-content',
    warning: 'border-warning/40 bg-warning-soft text-content',
    success: 'border-success/30 bg-success-soft text-content',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm leading-6 ${tones[tone]}`}>
      {title && <p className="font-medium">{title}</p>}
      <div className={title ? 'mt-0.5 text-content-muted' : ''}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy a text

/**
 * One value to paste into a console field. The whole box copies on tap — on a
 * phone the button is not the only target — and the counter warns before the
 * console does when a text is over Play's limit.
 */
export function CopyField({ label, value, limit, multiline }: {
  label: string; value: string | null | undefined; limit?: number; multiline?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  if (!value) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-3">
        <p className="text-xs font-medium text-content-muted">{label}</p>
        <p className="mt-0.5 text-sm text-content-subtle">Not in this package — ask whoever sent the link.</p>
      </div>
    );
  }
  const long = multiline && value.length > 280;
  const over = limit !== undefined && value.length > limit;

  async function copy() {
    if (await copyText(value!)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  }

  return (
    <div className={`overflow-hidden rounded-lg border transition-colors ${
      copied ? 'border-success' : 'border-border'} bg-surface`}>
      <div className="flex items-center gap-2 border-b border-border bg-surface-muted/50 py-1.5 pl-4 pr-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-content-muted">{label}</span>
        {limit !== undefined && (
          <span className={`text-[11px] tabular-nums ${over ? 'font-semibold text-danger' : 'text-content-subtle'}`}>
            {value.length}/{limit}
          </span>
        )}
        <button
          type="button"
          onClick={copy}
          className={`flex min-h-[32px] items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors ${
            copied ? 'bg-success text-white' : 'bg-primary text-white hover:bg-primary-600'}`}
        >
          {copied ? <IconCheck className="h-3.5 w-3.5" /> : <IconCopy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <button
        type="button"
        onClick={copy}
        title="Tap to copy"
        className="block w-full px-4 py-3 text-left hover:bg-surface-muted/40"
      >
        <span className={`block break-words text-sm text-content ${multiline ? 'whitespace-pre-wrap leading-6' : ''} ${
          long && !open ? 'max-h-36 overflow-hidden [mask-image:linear-gradient(to_bottom,black_60%,transparent)]' : ''}`}>
          {value}
        </span>
      </button>
      {long && (
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="w-full border-t border-border py-1.5 text-xs text-content-muted hover:text-content">
          {open ? 'Show less' : 'Show all text'}
        </button>
      )}
      {over && (
        <p className="border-t border-border bg-danger-soft px-4 py-1.5 text-xs text-danger">
          Over Play&apos;s {limit}-character limit — the console will not save it. Shorten it, or ask the sender.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pick an answer

/** A question the console asks and the answer to choose, as the console words it. */
export function Pick({ q, a, sub }: { q: string; a: string | string[]; sub?: string }) {
  const answers = Array.isArray(a) ? a : [a];
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <p className="text-xs text-content-muted">{q}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {answers.map((x) => (
          <span key={x} className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-sm font-medium text-primary dark:text-primary-300">
            <IconCheck className="h-3.5 w-3.5" />
            {x}
          </span>
        ))}
      </div>
      {sub && <p className="mt-1.5 text-xs text-content-subtle">{sub}</p>}
    </div>
  );
}

/** A long questionnaire where most answers are the same: one row per question. */
export function AnswerTable({ rows }: { rows: { q: string; a: string; yes?: boolean }[] }) {
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
      {rows.map((r, i) => (
        <div key={`${r.q}-${i}`} className="flex items-start justify-between gap-3 px-4 py-2.5">
          <span className="min-w-0 text-sm text-content">{r.q}</span>
          <span className={`shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${
            r.yes ? 'bg-warning-soft text-warning' : 'bg-surface-muted text-content-muted'}`}>
            {r.a}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files

export function DownloadButton({ href, children, primary, onClick, busy }: {
  href?: string; children: ReactNode; primary?: boolean; onClick?: () => void; busy?: boolean;
}) {
  const cls = `inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors disabled:opacity-50 ${
    primary
      ? 'bg-primary text-white hover:bg-primary-600'
      : 'border border-border bg-surface text-content hover:bg-surface-muted'}`;
  if (href) {
    return <a href={href} className={cls}><IconDownload />{children}</a>;
  }
  return (
    <button type="button" onClick={onClick} disabled={busy} className={cls}>
      <IconDownload />{busy ? 'Preparing…' : children}
    </button>
  );
}

/** One image to upload: what it is, how it looks, the file. */
export function FileTile({ title, sub, href, preview, wide }: {
  title: string; sub: string; href: string; preview: string; wide?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3">
      <a href={href} className="shrink-0" title="Download">
        <img src={preview} alt="" className={`${wide ? 'h-14 w-[7.2rem]' : 'h-14 w-14'} rounded-md border border-border object-cover`} />
      </a>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-content">{title}</p>
        <p className="text-xs text-content-subtle">{sub}</p>
      </div>
      <a href={href} title={`Download ${title.toLowerCase()}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-content hover:bg-surface-muted">
        <IconDownload />
      </a>
    </div>
  );
}
